import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Zap, Signal, Wifi } from 'lucide-react';
import { motion } from 'framer-motion';

const TarifCalculatorPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Handytarif-Rechner - Besten Mobilfunktarif finden | Easy Vertrag</title>
        <meta name="description" content="Finden Sie mit unserem Handytarif-Rechner den optimalen Mobilfunktarif. Vergleichen Sie 5G-Tarife von Telekom, Vodafone, O2 und mehr. Flexible Laufzeiten und beste Preise." />
        <meta name="keywords" content="Handytarif Rechner, Mobilfunktarif vergleichen, 5G Tarife, Handyvertrag finden, Tarif Calculator" />
        <link rel="canonical" href="https://easy4vertrag.de/angebote/tarif" />
        <meta property="og:title" content="Handytarif-Rechner | Easy Vertrag" />
        <meta property="og:description" content="Finden Sie mit unserem Rechner den optimalen Mobilfunktarif. Alle Anbieter - beste Preise." />
        <meta property="og:url" content="https://easy4vertrag.de/angebote/tarif" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Handytarif-Rechner", "item": "https://easy4vertrag.de/angebote/tarif"}
            ]
          }
        `}</script>
      </Helmet>
      <section className="bg-gradient-to-r from-green-900 to-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-4">Handytarif-Rechner</h1>
            <p className="text-xl text-blue-100">
              Finden Sie den optimalen Handytarif für Ihre Bedürfnisse
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-blue-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Signal className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">5G Netzwerk</h3>
              <p className="text-gray-600">
                Modernste Mobilfunktechnologie für schnelles Internet
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-green-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Flexible Laufzeit</h3>
              <p className="text-gray-600">
                Wählen Sie zwischen verschiedenen Vertragslaufzeiten
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-purple-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wifi className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Große Auswahl</h3>
              <p className="text-gray-600">
                Vergleichen Sie Tarife aller führenden Anbieter
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-orange-50 p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Schnelle Aktivierung</h3>
              <p className="text-gray-600">
                Einfache Online-Buchung und schnelle Aktivierung
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Maximale Breite für große Bildschirme, 90% für Tablets und iPads */}
          <div className="w-full max-w-none lg:max-w-[90vw] xl:max-w-[90vw] mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6 bg-green-600 text-white">
                <h2 className="text-2xl font-bold mb-2">Handytarif-Rechner</h2>
                <p>Finden Sie den perfekten Tarif für Ihre Bedürfnisse</p>
              </div>
              <div className="p-2 md:p-4 lg:p-6">
                <iframe
                  src="https://tools.communicationads.net/calc.php?cl=handytarif&wf=18676&country=DE"
                  className="w-full min-h-[800px] md:min-h-[900px] lg:min-h-[1000px] border-0"
                  title="Handytarif-Rechner"
                  style={{
                    transform: 'scale(1)',
                    transformOrigin: 'top left'
                  }}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TarifCalculatorPage;